import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

/**
 * GradientButton Component
 * Primary CTA button with gradient background and glow effect
 */
const GradientButton = ({
  children,
  to,
  href,
  onClick,
  variant = 'primary', // 'primary' | 'secondary' | 'outline'
  size = 'default', // 'small' | 'default' | 'large'
  icon,
  iconPosition = 'right',
  disabled = false,
  className = '',
  ...props
}) => {
  const sizeClasses = {
    small: 'px-4 py-2 text-sm',
    default: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg',
  };

  const variantClasses = {
    primary: `
      bg-gradient-subtle text-dark-primary font-semibold
      hover:shadow-glow-lg hover:-translate-y-0.5
      active:translate-y-0
    `,
    secondary: `
      bg-[var(--bg-tertiary)] text-[var(--text-primary)]
      hover:bg-[var(--bg-elevated)] hover:-translate-y-0.5
      active:translate-y-0
    `,
    outline: `
      bg-transparent border-2 border-accent text-accent
      hover:bg-accent/10
      hover:-translate-y-0.5
      active:translate-y-0
    `,
  };

  const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-semibold rounded-xl
    transition-all duration-300
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `.trim();

  const content = (
    <>
      {icon && iconPosition === 'left' && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span className="flex-shrink-0">{icon}</span>}
    </>
  );

  const MotionComponent = to ? motion(Link) : href ? motion.a : motion.button;

  return (
    <MotionComponent
      to={to}
      href={href}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      className={baseClasses}
      {...props}
    >
      {content}
    </MotionComponent>
  );
};

export default GradientButton;
